<template>
  <div class="invited-to-room-notification">
    <NotificationLayout>
      <template #icon>
        <UserIcon class="user-icon" :userId="isSent ? data.recipientId : data.senderId" userStatus="dark" />
      </template>

      <template #content>
        <div class="notification-header">
          <Name :data="data" />
        </div>
        <div class="notification-body">
          {{ message }}
          <div v-if="status" class="status" :class="[`status--${status.colour}`]">
            {{ status.text }}
          </div>
        </div>
      </template>

      <template #footer>
        <template v-if="canReply">
          <transition name="fade">
            <div key="closed" v-if="!messageOpen && !sendingMessage">
              <FooterButtons :buttons="footerButtons" />
            </div>
            <div key="open" class="open-message" v-else-if="messageOpen">
              <div class="message-presets">
                <button v-for="(message, i) in presetMessages" :key="i" class="message-preset" @click="pickPresetMessage(i)">
                  {{ message }}
                </button>
              </div>
              <div class="divider">or</div>
              <TextInput class="custom-message" multiline label="Type a custom message" v-model="customMessage" />

              <footer class="open-message__footer">
                <img @click="back" class="back-arrow" src="@/assets/sidebar-icons/close-arrow.svg" alt="previous step" />
                <btn :disabled="!isMessageValid" @onClick="sendMessage()" colour="green">
                  <template #text>Send</template>
                </btn>
              </footer>
            </div>
            <div key="sending" class="sending-message" v-else>Sending...</div>
          </transition>
        </template>

        <div v-if="response" class="drawer">
          <div class="drawer__left"></div>
          <div class="drawer__right">
            {{ response }}
          </div>
        </div>

        <cancel-button v-if="isSent && data.status === 'sent'" class="cancel-invite" :notification="data" />
      </template>
    </NotificationLayout>
  </div>
</template>
<script>
// Wrapper for a sidebar notification.
import { mapGetters, mapActions } from 'vuex';
import Name from './parts/Name.vue';
import FooterButtons from './parts/FooterButtons.vue';
import UserIcon from '@/components/UserIcon.vue';
import CancelButton from '@/components/notifications/CancelButton.vue'
import NotificationLayout from './parts/NotificationLayout.vue';
import { TextInput } from '@/framework';
import { invitePresetMessages } from '@/data/invitePresetMessages';

export default {
  name: 'InvitedToRoom',
  props: {
    data: Object
  },
  data() {
    return {
      messageOpen: false,
      customMessage: '',
      presetMessages: invitePresetMessages,
      sendingMessage: false
    };
  },
  components: {
    NotificationLayout,
    UserIcon,
    Name,
    FooterButtons,
    TextInput,
    CancelButton
  },
  computed: {
    ...mapGetters({
      getNotificationMeta: 'getNotificationMeta',
    }),
    meta() {
      return this.getNotificationMeta(this.data);
    },
    title() {
      return this.data.title;
    },
    message() {
      return this.data.message;
    },
    isSent() {
      return this.data.sentOrReceived === 'sent';
    },
    canReply() {
      return !this.isSent && this.data.status === 'sent';
    },
    meetingEnded() {
      return this.data.status === 'missed';
    },
    cancelled() {
      return this.data.status === 'cancelled';
    },
    hasResponse() {
      return Boolean(this.data.response);
    },
    isMessageValid() {
      return this.customMessage.length > 0 && this.customMessage.length < 1000;
    },
    response() {
      if (this.data.status == "custom") {
        return this.data.response
      } else return ''
    },
    status() {
      if (this.isSent) {
        if (this.data.status == 'accepted') {
          return {
            text: 'They accepted',
            colour: 'green'
          };
        }
                                              // Custom message is only ever declined
        if (this.data.status == 'declined' || this.data.status == "custom") {
          return {
            text: 'They declined',
            colour: 'red'
          };
        }

        if (this.data.status == 'missed') {
          return {
            text: 'They missed this invite',
            colour: 'grey'
          };
        }
        if (this.data.status == 'cancelled') {
          return {
            text: 'You cancelled this invite',
            colour: 'red'
          };
        }
        return ''
      } else {
        if (this.data.status == 'accepted') {
          return {
            text: 'You accepted',
            colour: 'green'
          };
        }

        if (this.data.status == 'declined' || this.data.status == "custom") {
          return {
            text: 'You declined',
            colour: 'red'
          };
        }

        if (this.data.status == 'missed') {
          return {
            text: 'You missed this invite',
            colour: 'grey'
          };
        }

        if (this.data.status == 'cancelled') {
          return {
            text: 'They cancelled this invite',
            colour: 'red'
          };
        }
        return ''
      }
    },
    footerButtons() {
      return [
        {
          label: 'Decline',
          colour: 'red',
          icon: require(`@/assets/global-icons/cross-white.svg`),
          onClick: this.onDecline
        },
        {
          icon: require('@/assets/sidebar-icons/chat.svg'),
          label: 'Message',
          colour: 'grey',
          onClick: this.onMessage
        },
        {
          label: 'Accept',
          colour: 'green',
          icon: require(`@/assets/notification-icons/camcorder-white.svg`),
          onClick: this.onAccept
        },
      ];
    }
  },
  methods: {
    ...mapActions(['acceptInviteHandler', 'declineInviteHandler', 'messageInviteHandler']),
    onAccept() {
      this.sendingMessage = true;
      this.acceptInviteHandler(this.data);
    },
    onDecline() {
      this.sendingMessage = true;
      this.declineInviteHandler(this.data);
    },
    onMessage() {
      this.messageOpen = true;
    },
    back() {
      this.messageOpen = false;
    },
    pickPresetMessage(index) {
      this.customMessage = this.presetMessages[index];
      this.sendMessage();
    },
    async sendMessage() {
      this.sendingMessage = true;
      this.messageOpen = false;
      await this.messageInviteHandler([this.data, this.customMessage]);
    }
  },
  watch: {
    data() {
      this.sendingMessage = false;
    }
  }
};
</script>
<style scoped lang="scss">
@import '~@/scss/components/notifications/notification_styles.scss';

.user-icon {
  box-sizing: border-box;
  width: 45px;
  height: 45px;
}

.fade-enter-active,
.fade-leave-active {
  transition: 0.2s;
}

.fade-enter {
  max-height: 0px;
}

.fade-enter-to {
  max-height: 500px;
}

.fade-leave {
  max-height: 500px;
}

.fade-leave-to {
  max-height: 0;
}

.open-message {
  padding: 0 15px 0 15px;

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    margin-bottom: 10px;
  }
}

.back-arrow {
  cursor: pointer;
  height: 32px;
  width: 32px;
}

.message-presets {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 5px;
}

.status {
  margin-top: 10px;
  font-weight: bold;

  &--green {
    color: #56ae27;
  }

  &--red {
    color: $red;
  }

  &--grey {
    color: $grey-3;
  }
}

.drawer {
  display: flex;
  background-color: #414141;
  border-radius: 0 0 17px 17px;

  &__left {
     width: 70px;
     flex-shrink: 0;
     height: 100%;
  }

  &__right {
    flex-grow: 1;
    font-style: italic;
    font-weight: bold;
    font-size: 0.75rem; /** 12px; **/
    color: white;
    line-height: 1rem;
    padding: 15px 20px 15px 0;
  }
}
.cancel-invite {
  padding: 0.5rem;
  border-radius: 0 0 17px 17px;
  font-size: 90%;
}

.divider {
  display: flex;
  justify-content: center;
  align-items: center;
  color: $grey-3;
  font-weight: bold;
  margin-top: 10px;

  &:before,
  &:after {
    content: '';
    display: block;
    height: 1px;
    width: 50px;
    background-color: $grey-3;
    margin-right: 10px;
    margin-left: 10px;
    position: relative;
    top: 2px;
  }
}

.message-preset {
  border: none;
  background-color: $grey-7;
  color: $grey-3;
  font-size: 0.875rem; /** 14px */
  width: 100%;
  display: block;
  padding: 8px;
  border-radius: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background-color: $grey-3;
    color: white;
  }
}

.custom-message {
  margin-top: 10px;
  box-sizing: border-box;
  width: 100%;
}

.sending-message {
  color: $grey-2;
  text-align: center;
  padding: 20px;
  font-weight: bold;
}
</style>
